<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, watch, onMounted, computed, type ComputedRef } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import AoeSingleLineEntry from './AoeSingleLineEntry.vue'
import AoeCategoryEntry from './AoeCategoryEntry.vue'
import AoeCatalogEntry from './AoeCatalogEntry.vue'
import type { IAoeCart, IAoeCartItem } from '../../types/CartTypes'

import {
  TabStrip as kTabStrip,
  type TabStripSelectEventArguments,
} from '@progress/kendo-vue-layout'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { cartIcon } from '@progress/kendo-svg-icons'
import { Drawer, DrawerItem } from '@progress/kendo-vue-layout'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { FormatCurrency } from '../../services/currencyFormatter'
import {
  GetCartProductSubtotal,
  GetCartTotalBuyingDiscountAmount,
} from '../../services/CartTotalsService'
import Swal from 'sweetalert2'
import { handleError } from '../../services/ErrorService.ts'

import type { IProductAttributes } from '../../types/SkuTypes'
import { GetCachedProductAttributes } from '../../services/SkuService.ts'
import PricePerUnitWeight from '../PricePerUnitWeight.vue'

import {
  DeleteItemFromCart,
  UpdateItemInCart,
} from '../../services/CartService'
import { GetCachedProductDetails } from '../../services/SkuService.ts'

const logger = useLogger()

const selectedTab = ref(0)
const cartItems = ref<IAoeCartItem[]>([])
const cartSubtotal = ref<number>(0)

const productAttributesByProductCode = ref<Record<string, IProductAttributes | null>>({})

// Computed property for total number of items in cart
const cartCount = computed(() =>
  cartItems.value.reduce((total, item) => total + item.quantity, 0),
)

const cartItemsWithoutDiscountIds: ComputedRef<IAoeCartItem[]> = computed(
  () => {
    return cartItems.value.filter(
      item =>
        !item.itemPoeDiscPlanLevelDiscIds ||
        item.itemPoeDiscPlanLevelDiscIds.length === 0,
    )
  },
)

const cartItemsWithDiscountIds: ComputedRef<IAoeCartItem[]> = computed(() => {
  return cartItems.value.filter(
    item =>
      item.itemPoeDiscPlanLevelDiscIds &&
      item.itemPoeDiscPlanLevelDiscIds.length > 0,
  )
})

function GetAwardMessage(cartItem: IAoeCartItem): string {
  if (!props.cart?.earnedDiscounts || props.cart.earnedDiscounts.length === 0)
    return ''

  const poeDiscPlanLevelDiscIds =
    cartItem.itemPoeDiscPlanLevelDiscIds ?? cartItem.poeDiscPlanLevelDiscIds ?? []

  for (const poeDiscPlanLevelDiscId of poeDiscPlanLevelDiscIds) {
    const earnedDiscount = props.cart.earnedDiscounts.find(
      ed => ed.poeDiscPlanLevelDiscId === poeDiscPlanLevelDiscId,
    )
    if (earnedDiscount?.awardMessage) return earnedDiscount.awardMessage
  }

  return ''
}

const tabs = [
  {
    title: t('partyOrderEntry.SingleLineEntry'),
    content: 'SingleLineEntry',
  },
  {
    title: t('partyOrderEntry.CategoryEntry'),
    content: 'CategoryEntry',
  },
  {
    title: t('partyOrderEntry.CatalogEntry'),
    content: 'CatalogEntry',
  },
]

const emit = defineEmits([
  'update-cart',
  'updateProductEntryTab',
  'update-selected-category',
  'proceed-to-checkout',
])

const props = defineProps<{
  cart: IAoeCart | null
  showheader: boolean
  poediscplanleveldiscid?: number
  ordertypeid: string
  entryselectedtab?: number
  entryselectedcategory?: string
  istwosteps?: boolean
  productimageurls?: Record<string, string>
  disabled?: boolean
}>()

// Derived from the cart items - see CartTotalsService for why these are not
// taken from the cart-level fields returned by the server.
const cartProductTotal = computed(() => GetCartProductSubtotal(props.cart))
const cartDiscountTotal = computed(() =>
  GetCartTotalBuyingDiscountAmount(props.cart),
)

watch(
  () => props.cart,
  async newCart => {
    logger.error('cart changed in AoeTabbedProductEntry', newCart)
    cartItems.value = newCart?.items ?? []
    cartSubtotal.value = newCart?.subtotal ?? 0
    logger.error('cartItems', cartItems.value)

    const uniqueProductCodes = Array.from(
      new Set((newCart?.items ?? []).map(i => i.productCode).filter(Boolean)),
    )
    if (uniqueProductCodes.length > 0) {
      GetCachedProductAttributes(uniqueProductCodes, 'PricePerUnitWeight')
        .then(attributes => {
          const next: Record<string, IProductAttributes | null> = {}
          for (const attr of attributes ?? []) {
            const productCode = attr?.item?.productCode
            const attributeValue = attr?.item?.attributeValue
            if (!productCode) continue

            const parsed = attributeValue != null ? Number(attributeValue) : NaN
            if (Number.isFinite(parsed) && parsed > 0) {
              next[productCode] = { CatalogImage: '', PricePerUnitWeight: parsed }
            } else {
              next[productCode] = null
            }
          }
          productAttributesByProductCode.value = next
        })
        .catch(error => {
          logger.error('GetCachedProductAttributes PricePerUnitWeight', error)
        })
    }
  },
  { deep: true },
)

onMounted(async () => {
  logger.debug('AoeTabbedProductEntry onMounted')
  cartItems.value = props.cart?.items ?? []
  cartSubtotal.value = props.cart?.subtotal ?? 0

  const uniqueProductCodes = Array.from(
    new Set((props.cart?.items ?? []).map(i => i.productCode).filter(Boolean)),
  )
  logger.debug(
    'AoeTabbedProductEntry uniqueProductCodes',
    'count=',
    uniqueProductCodes.length,
    'preview=',
    uniqueProductCodes.slice(0, 20),
  )
  if (uniqueProductCodes.length > 0) {
    try {
      const attributes = await GetCachedProductAttributes(
        uniqueProductCodes,
        'PricePerUnitWeight',
      )
      const next: Record<string, IProductAttributes | null> = {}
      for (const attr of attributes ?? []) {
        const productCode = attr?.item?.productCode
        const attributeValue = attr?.item?.attributeValue
        if (!productCode) continue

        const parsed = attributeValue != null ? Number(attributeValue) : NaN
        if (Number.isFinite(parsed) && parsed > 0) {
          next[productCode] = { CatalogImage: '', PricePerUnitWeight: parsed }
        } else {
          next[productCode] = null
        }
      }
      productAttributesByProductCode.value = next
    } catch (error) {
      logger.error('GetCachedProductAttributes PricePerUnitWeight', error)
    }
  }
})

function onSelectHandler(event: TabStripSelectEventArguments) {
  selectedTab.value = event.selected
  emit('updateProductEntryTab', selectedTab.value)
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler AoeTabbedProductEntry', event)
  emit('update-cart', event)
}

function goToCheckout() {
  expanded.value = !expanded.value
  emit('proceed-to-checkout')
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    props.productimageurls,
  )
  if (props.productimageurls == undefined) return '#'

  const productImageUrl = props.productimageurls[productCode]
  if (productImageUrl) return productImageUrl

  return '#'
}

function updateSelectedCategoryHandler(selectedCategory: string) {
  logger.debug(
    'updateSelectedCategoryHandler AoeTabbedProductEntry',
    selectedCategory,
  )
  emit('update-selected-category', selectedCategory)
}

function cartIconClickedHandler() {
  logger.debug('cartIconClickedHandler AoeTabbedProductEntry')

  if (!props.istwosteps) {
    const yOffset = -10
    const element = document.getElementsByClassName(
      'shopping-cart-section-header',
    )

    if (element != null) {
      let closestY = 0
      for (const el of element) {
        const y = el.getBoundingClientRect().top + window.scrollY + yOffset
        if (closestY == 0 || Math.abs(y) < Math.abs(closestY)) {
          closestY = y
        }
      }
      window.scrollTo({ top: closestY, behavior: 'smooth' })
    }
  } else {
    cartDrawerClick()
  }
}

const expanded = ref(false)
const drawerItems = ref([
  {
    text: t('cart.ViewCart'),
    selected: false,
    data: { path: '/' },
  },
])

function cartDrawerClick() {
  logger.debug('cartDrawerClick')
  expanded.value = !expanded.value
}

async function deleteItemClickedHandler(lineNumber: number) {
  logger.debug('deleteItemClickedHandler', lineNumber)
  if (!props.cart) {
    logger.warn('cart is null')
    return
  }
  await DeleteItemFromCart({
    aoeCartIdToken: props.cart.aoeCartIdToken,
    lineNumber,
  })
  emit('update-cart', event)
}

async function GetAvailableQty(cartItem: IAoeCartItem, currentCartQty: number) {
  const productDetails = await GetCachedProductDetails(props.ordertypeid, [
    cartItem.skuCode,
  ])
  if (
    productDetails == null ||
    productDetails.length == 0 ||
    productDetails[0].skus == null ||
    productDetails[0].skus.length == 0 ||
    productDetails[0].skus[0].inventoryLevel == undefined
  )
    return 0

  const sku = productDetails[0].skus[0]
  const inventoryLevel = sku.inventoryLevel

  // If OutOfStockVisible is true, ignore inventory level checks
  if (sku.outOfStockVisible === true) {
    return inventoryLevel.availableQty - currentCartQty
  }

  // Otherwise check inventory level normally
  if (inventoryLevel.inventoryLevel === 'OutOfStock' || inventoryLevel.inventoryLevel === 'StopSell') {
    return 0
  }

  return inventoryLevel.availableQty - currentCartQty
}

async function minusClickedHandler(lineNumber: number) {
  logger.debug('minusClickedHandler', lineNumber)
  if (!props.cart) {
    logger.warn('cart is null')
    return
  }
  const cartItem = props.cart?.items.find(
    item => item.lineNumber === lineNumber,
  )
  if (!cartItem) {
    logger.warn(
      'cartItem is null for aoeCartId',
      props.cart?.aoeCartId,
      'linenumber',
      lineNumber,
    )
    return
  }
  if (cartItem.quantity && cartItem.quantity > 1) {
    const quantity = cartItem.quantity - 1
    try {
      await UpdateItemInCart({
        aoeCartIdToken: props.cart?.aoeCartIdToken,
        lineNumber,
        quantity,
      })
    } catch (error) {
      logger.error('minusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }
    emit('update-cart', event)
  }
}

async function plusClickedHandler(lineNumber: number) {
  logger.debug('plusClickedHandler', lineNumber)
  if (!props.cart) {
    logger.warn('cart is null')
    return
  }
  const cartItem = props.cart?.items.find(
    item => item.lineNumber === lineNumber,
  )
  if (!cartItem) {
    logger.warn(
      'cartItem is null for aoeCartId',
      props.cart?.aoeCartId,
      'linenumber',
      lineNumber,
    )
    return
  }
  let quantity = 1
  if (cartItem.quantity) quantity = cartItem.quantity + 1

  let canAddToCart = true

  // Skip availability check if OutOfStockVisible is true
  if (!cartItem.outOfStockVisible) {
    const availableQty = await GetAvailableQty(cartItem, cartItem.quantity)
    if (availableQty < 1) {
      canAddToCart = false
    }
  }

  if (canAddToCart) {
    try {
      await UpdateItemInCart({
        aoeCartIdToken: props.cart?.aoeCartIdToken,
        lineNumber,
        quantity,
      })
    } catch (error) {
      logger.error('plusClickedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }
    emit('update-cart', event)
  } else {
    Swal.fire({
      title: t('alerts.AddToCartError'),
      text: t('alerts.NoQuantityAvailable'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })

    emit('update-cart', event)
  }
}

async function cartItemQuantityChangedHandler(
  event: unknown,
  cartItem: IAoeCartItem,
) {
  logger.debug('cartItemQuantityChangedHandler', event, cartItem)
  if (!props.cart) {
    logger.warn('cart is null')
    return
  }

  let canAddToCart = true

  // Skip availability check if OutOfStockVisible is true
  if (!cartItem.outOfStockVisible) {
    const availableQty = await GetAvailableQty(cartItem, 0)
    if (availableQty < cartItem.quantity) {
      canAddToCart = false
    }
  }

  if (canAddToCart && cartItem.quantity > 0) {
    try {
      await UpdateItemInCart({
        aoeCartIdToken: props.cart?.aoeCartIdToken,
        lineNumber: cartItem.lineNumber,
        quantity: cartItem.quantity,
      })
    } catch (error) {
      logger.error('cartItemQuantityChangedHandler UpdateItemInCart failed', error)
      await handleError(error, t)
    }

    emit('update-cart', event)
  } else if (cartItem.quantity === 0) {
    logger.warn('cartItem.quantity is 0')
    emit('update-cart', event)
  } else {
    Swal.fire({
      title: t('alerts.AddToCartError'),
      text: t('alerts.NoQuantityAvailable'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay'),
    })

    emit('update-cart', event)
  }
}

async function cartItemQuantityInputHandler(
  event: { value: string },
  cartItem: IAoeCartItem,
) {
  logger.debug('cartItemQuantityInputHandler', event, cartItem, event.value)
  if (event.value === null || event.value.length === 0) {
    logger.warn('event.value is undefined')
    cartItem.quantity = 0
    return
  }
  const quantity = parseInt(event.value)
  logger.debug('quantity', quantity)
  if (quantity > 0) {
    cartItem.quantity = quantity
  }
}
</script>

<template>
  <div class="aoe-tabbed-product-entry" style="position: relative">
    <div class="cart-anchor-icon">
      <KButton
        :svg-icon="cartIcon"
        :fill-mode="'clear'"
        :disabled="props.disabled"
        @click="cartIconClickedHandler"
      ></KButton>
      <div v-if="cartCount > 0" class="cart-count-badge">{{ cartCount }}</div>
    </div>
    <kTabStrip
      :selected="entryselectedtab ? entryselectedtab : selectedTab"
      tab-position="top"
      @select="onSelectHandler($event)"
      :tabs="tabs"
      class="tabs-product-entry"
    >
      <template v-slot:SingleLineEntry>
        <AoeSingleLineEntry
          :cart="props.cart ?? null"
          :showheader="true"
          :poediscplanleveldiscid="props.poediscplanleveldiscid"
          @update-cart="updateCartHandler($event)"
        >
        </AoeSingleLineEntry>
      </template>
      <template v-slot:CategoryEntry>
        <AoeCategoryEntry
          :cart="props.cart ?? null"
          :showheader="true"
          :ordertypeid="props.ordertypeid"
          :entryselectedcategory="props.entryselectedcategory"
          @update-cart="updateCartHandler($event)"
          @update-selected-category="updateSelectedCategoryHandler"
        >
        </AoeCategoryEntry>
      </template>
      <template v-slot:CatalogEntry>
        <AoeCatalogEntry
          :cart="props.cart ?? null"
          :showheader="true"
          :ordertypeid="props.ordertypeid"
          :entryselectedcategory="props.entryselectedcategory"
          @update-cart="updateCartHandler($event)"
          @update-selected-category="updateSelectedCategoryHandler"
        >
        </AoeCatalogEntry>
      </template>
    </kTabStrip>

    <Drawer
      :expanded="expanded"
      :position="'end'"
      :mode="'overlay'"
      :mini="false"
      @overlayclick="cartDrawerClick"
      :items="drawerItems"
      :item="'CustomItem'"
      class="drawer-component cart-summary-drawer"
    >
      <template v-slot:CustomItem="{ props }">
        <DrawerItem v-bind="props" @click="props.onClick">
          <div class="cart-drawer-container">
            <div class="cart-drawer-header">
              {{ t('consultantOrderEntry.CartSummaryHeader') }}
            </div>
            <div class="cart-items mt-3">
              <div
                class="cart-item"
                v-for="cartItem in cartItemsWithoutDiscountIds"
                :key="cartItem.lineNumber"
              >
                <div class="item-row">
                  <!-- Product Image -->
                  <div
                    v-if="
                      GetFullProductImageUrlFromStringDictionary(
                        cartItem.productCode,
                      ) !== '#'
                    "
                    class="item-image"
                  >
                    <img
                      :src="
                        GetFullProductImageUrlFromStringDictionary(
                          cartItem.productCode,
                        )
                      "
                      class="img-fluid"
                      :alt="t('cart.ProductImageAlt')"
                    />
                  </div>

                  <!-- Product Info and Controls -->
                  <div class="item-details">
                    <div class="item-header">
                      <div class="product-info">
                        <div class="cartSkuName">{{ cartItem.skuName }}</div>
                        <div class="cartSkuCode">#{{ cartItem.skuCode }}</div>
                      </div>
                      <div class="remove-button">
                        <KButton
                          :fill-mode="'clear'"
                          :disabled="props.disabled"
                          @click="deleteItemClickedHandler(cartItem.lineNumber)"
                          class="remove-item-btn"
                          >&#x2715;</KButton
                        >
                      </div>
                    </div>

                    <div class="item-controls">
                      <!-- Quantity Control -->
                      <div class="quantity-control">
                        <button
                          class="minus"
                          :disabled="props.disabled"
                          @click="minusClickedHandler(cartItem.lineNumber)"
                        >
                          &minus;
                        </button>
                        <KTextBox
                          class="quantity"
                          :model-value="cartItem.quantity"
                          :disabled="props.disabled"
                          @change="
                            cartItemQuantityChangedHandler($event, cartItem)
                          "
                          @input="
                            cartItemQuantityInputHandler($event, cartItem)
                          "
                        />
                        <button
                          class="plus"
                          :disabled="props.disabled"
                          @click="plusClickedHandler(cartItem.lineNumber)"
                        >
                          &plus;
                        </button>
                      </div>

                      <!-- Price Display -->
                      <div class="price-display">
                        <div class="cartItemPrice">
                          {{
                            FormatCurrency(
                              cartItem.price,
                              cart?.currencyId || null,
                            )
                          }}
                        </div>
                        <PricePerUnitWeight
                          :RetailPrice="cartItem.retailPrice"
                          :SalePrice="cartItem.price"
                          :IProductAttributes="productAttributesByProductCode[cartItem.productCode] ?? null"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="cart-item"
                v-for="cartItem in cartItemsWithDiscountIds"
                :key="cartItem.lineNumber"
              >
                <div class="item-row">
                  <!-- Product Image -->
                  <div
                    v-if="
                      GetFullProductImageUrlFromStringDictionary(
                        cartItem.productCode,
                      ) !== '#'
                    "
                    class="item-image"
                  >
                    <img
                      :src="
                        GetFullProductImageUrlFromStringDictionary(
                          cartItem.productCode,
                        )
                      "
                      class="img-fluid"
                      :alt="t('cart.ProductImageAlt')"
                    />
                  </div>

                  <!-- Product Info and Controls -->
                  <div class="item-details">
                    <div class="item-header">
                      <div class="product-info">
                        <div class="cartSkuName">{{ cartItem.skuName }}</div>
                        <div class="cartSkuCode">#{{ cartItem.skuCode }}</div>
                      </div>
                    </div>

                    <div class="item-controls">
                      <!-- Quantity Control -->
                      <div class="quantity-control">
                        <KTextBox
                          class="quantity"
                          :model-value="cartItem.quantity"
                          :readonly="true"
                        />
                      </div>

                      <!-- Price Display -->
                      <div class="price-display">
                        {{ GetAwardMessage(cartItem) }}
                        <span class="strike-through cart-item-retail-price">{{
                          FormatCurrency(cartItem.price, null)
                        }}</span
                        ><span class="cart-item-price">{{
                          FormatCurrency(
                            cartItem.totalDiscountAmount
                              ? cartItem.price - (cartItem.totalDiscountAmount / cartItem.quantity)
                              : cartItem.price,
                            null,
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="cart-drawer-footer mt-3">
              <div class="cart-totals-container">
                <div class="cart-total-row">
                  <div class="lblProductTotal">
                    {{ t('cart.ProductTotal') }}
                  </div>
                  <div class="lblProductTotalValue text-input-currency-display">
                    {{
                      FormatCurrency(cartProductTotal, cart?.currencyId || null)
                    }}
                  </div>
                </div>
                <div class="cart-total-row">
                  <div class="lblDiscountTotal">
                    {{ t('cart.DiscountTotal') }}
                  </div>
                  <div class="lblDiscountTotalValue text-input-currency-display">
                    {{
                      FormatCurrency(cartDiscountTotal, cart?.currencyId || null)
                    }}
                  </div>
                </div>
                <div class="cart-total-row">
                  <div class="lblSubtotal">
                    {{ t('cart.Subtotal') }}
                  </div>
                  <div class="lblSubtotalValue text-input-currency-display">
                    {{ FormatCurrency(cartSubtotal, cart?.currencyId || null) }}
                  </div>
                </div>
              </div>
              <div class="mt-4 col-12 text-center">
                <KButton
                  class="baseBtn primaryBtn proceed-to-checkout-button"
                  :disabled="props.disabled"
                  @click="goToCheckout"
                  >{{ t('consultantOrderEntry.ProceedToCheckout') }}
                </KButton>
              </div>
            </div>
          </div>
        </DrawerItem>
      </template>
    </Drawer>
  </div>
</template>

<style lang="scss">
.aoe-tabbed-product-entry .text-input-currency-display .k-input {
  border: none;
  color: black;
}

.aoe-tabbed-product-entry .text-input-currency-display .k-input-inner {
  text-align: right;
}

.quantity-control .k-textbox {
  text-align: center;
}

@media (max-width: 768px) {
  .aoe-tabbed-product-entry {
    .tabs-product-entry {
      .k-tabstrip-items-wrapper {
        display: block;
        font-size: 1rem;

        .k-tabstrip-items .k-link {
          padding: 0;
        }
      }
    }
  }
}

.aoe-tabbed-product-entry .cart-anchor-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin-right: 10px;

  @media (max-width: 768px) {
    position: static;

    display: flex;
    justify-content: end;

    .cart-count-badge {
      right: 0px;
    }
  }
}

.cart-count-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--vt-c-teal-dark);
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  @include typography-caption;
  @include font-weight-bold;
  justify-content: center;
  padding: 0 4px;
}

/* Drawer container styles */
.aoe-tabbed-product-entry .cart-drawer-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
}

/* Header styles */
.cart-drawer-header {
  @include font-weight-bold;
  padding: $spacing-4 0;
  flex-shrink: 0; /* Don't allow header to shrink */
}

/* Scrollable cart items area — grows with content, scrolls only when it must */
.aoe-tabbed-product-entry .cart-drawer-container .cart-items {
  flex: 0 1 auto;
  overflow-y: auto;
  min-height: 0; /* Allow flex item to shrink below content size */
  padding-right: 5px;

  .minus .plus {
    padding: $spacing-2-5 $spacing-1;
  }
}

/* Footer with checkout button — hugs items, pinned at bottom when items overflow */
.aoe-tabbed-product-entry .cart-drawer-footer {
  padding: $spacing-4 0;
  flex-shrink: 0;
  background-color: white;
  margin-top: auto;
}

.aoe-tabbed-product-entry .k-drawer-item {
  display: flow;
  height: 100vh; /* Make the drawer item full height */
  overflow: hidden; /* Prevent outer scrolling */
}

/* !important required: Override Kendo's inline style width with responsive min() */
.aoe-tabbed-product-entry .k-drawer-wrapper {
  width: min(80vw, 600px) !important;
}

/* Collapsed drawer container must not take layout space or show as a white panel */
.aoe-tabbed-product-entry .k-drawer-container {
  position: relative;
  width: 0 !important;
  height: 0 !important;
  overflow: visible !important;
  pointer-events: none;
}

/* Re-enable pointer events for drawer children */
.aoe-tabbed-product-entry .k-drawer-container > * {
  pointer-events: auto;
}

/* Drawer wrapper: fixed overlay panel, hidden when collapsed */
.aoe-tabbed-product-entry .k-drawer-wrapper {
  width: min(80vw, 600px) !important;
  position: fixed !important;
  right: 0;
  top: 0;
  bottom: 0;
  display: none;
  overflow: hidden;
  z-index: $z-drawer;
}

/* Show drawer wrapper when expanded */
.aoe-tabbed-product-entry .k-drawer-container.k-drawer-expanded .k-drawer-wrapper {
  display: block !important;
}

/* The visible panel is .k-drawer-wrapper (fixed): .k-drawer-container is forced to 0x0
   above, so the theme background on .k-drawer never paints. The panel needs its own
   opaque fill instead of relying on the :focus/:hover states Kendo puts on
   .k-drawer-item. */
/* !important required: Kendo theme CSS is not layered and would override this fill */
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-wrapper {
  background-color: var(--vt-c-white) !important;
}

/* This DrawerItem is a content panel, not a menu entry — neutralize Kendo's nav-item
   hover/focus/selected states so the wrapper background is the only one visible.
   Same pattern as AoeCartDrawer.vue. */
/* !important required: Kendo theme CSS is not layered and outranks @layer ascend */
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item,
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item:hover,
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item.k-hover,
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item:focus,
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item.k-focus,
.aoe-tabbed-product-entry .cart-summary-drawer .k-drawer-item.k-selected {
  background-color: transparent !important;
  color: inherit !important;
  box-shadow: none !important;
  cursor: auto !important;
}

/* Style the drawer overlay to not interfere with clicks when collapsed */
.aoe-tabbed-product-entry .k-overlay {
  pointer-events: auto; /* Enable pointer events only when drawer is expanded */
  z-index: $z-drawer-overlay; /* Keep overlay at a high z-index when visible */
}

/* Hide overlay when drawer is not expanded */
.aoe-tabbed-product-entry .k-drawer:not(.k-drawer-expanded) + .k-overlay {
  display: none !important;
}

/* Make cart icon appear under drawer when drawer is expanded */
.aoe-tabbed-product-entry .drawer-component.k-drawer-expanded,
.aoe-tabbed-product-entry
  .drawer-component.k-drawer-expanded
  .k-drawer-content {
  z-index: $z-drawer !important;
}

.aoe-tabbed-product-entry .drawer-component.k-drawer-expanded + .k-overlay {
  z-index: $z-drawer-backdrop !important;
}

.aoe-tabbed-product-entry .cart-anchor-icon .k-svg-icon,
.aoe-tabbed-product-entry
  .cart-anchor-icon
  .k-button-md.k-icon-button
  .k-button-icon.k-svg-icon
  > svg,
.aoe-tabbed-product-entry
  .cart-anchor-icon
  .k-button-md.k-icon-button
  .k-button-icon.k-icon-md
  > svg {
  width: 50px;
  height: 50px;
}

.minus,
.plus {
  @include typography-h3;
  border: none;
  background-color: white !important;
  line-height: 30px !important;
  padding: $spacing-2-5;
  vertical-align: middle !important;
}

.quantity input {
  text-align: center !important;
}

.cartSkuName {
  @include typography-body;
  @include font-weight-semibold;
  font-style: normal;
}

.cartSkuCode {
  @include typography-caption;
  @include font-weight-regular;
  color: rgba(#282727, 0.7);
  font-style: normal;
}

.cartItemPrice {
  @include typography-h4;
  @include font-weight-medium;
  font-style: normal;
  letter-spacing: 0.6px;
}

.cartItemInfo {
  margin: auto;
}

.cartItemInfoQty {
  margin: auto;
  white-space: nowrap;
}

@media screen and (min-width: 769px) {
  /* Cart Drawer Styles */
  .cart-drawer-container {
    padding: $spacing-4;
    height: 100vh;
  }
}

/* Cart totals container */
.cart-totals-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cart-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.cart-total-row .lblProductTotal,
.cart-total-row .lblDiscountTotal,
.cart-total-row .lblSubtotal {
  @include font-weight-semibold;
  flex-shrink: 0;
}

.cart-total-row .lblProductTotalValue,
.cart-total-row .lblDiscountTotalValue,
.cart-total-row .lblSubtotalValue {
  @include font-weight-medium;
  text-align: right;
  flex-shrink: 0;
}

@media screen and (max-width: 768px) {
  /* Cart Drawer Styles */
  .cart-drawer-container {
    padding: $spacing-1;
  }

  .cart-total-row {
    @include typography-small;
  }
}
.quantity-control .k-textbox {
  width: 50px !important;
  margin: 0 5px;
}

.cart-item {
  border-bottom: 1px solid #e9ecef;
  padding: $spacing-4 0;
}

.item-row {
  display: flex;
  gap: $spacing-3;
}

.item-image {
  width: 80px;
  height: 80px;
}

.item-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
  border: 1px solid #e9ecef;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.item-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.remove-item-btn {
  padding: 0;
  color: #999999;
}

.item-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.quantity-control {
  display: flex;
  align-items: center;
}

.quantity {
  width: 40px !important;
  margin: 0 5px;
}

.quantity input {
  text-align: center !important;
  border: 1px solid #e0e0e0;
  height: 36px;
}

.price-display {
  text-align: right;
}

.price-display .cartItemPrice {
  @include font-weight-medium;
  color: #000;
}

.price-display .k-numerictextbox {
  border: none;
}

.cart-anchor-icon {z-index:1;}

.aoe-tabbed-product-entry .k-widget.k-drawer {
  transform: none !important;
}
</style>
